/**
 * @file BlendWorkBar.js
 * @version 2.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-09-10
 * @description One bar over the calculator: which blend is loaded, what it is
 *              being compared against, and the two settings that change every
 *              figure on the screen.
 *
 *              A is the blend you are working on and B is what you are holding
 *              it against, which is the order you do them in: choose A first —
 *              load something to start from, or leave it blank and build up —
 *              then optionally pick a B to compare with.
 *
 *              Each dropdown carries its letter and shows the name of what it
 *              holds, which is what let the separate context chip and the A/B
 *              legend go: a control that displays its own value does not need a
 *              second element restating it.
 *
 *              The dirty marker rides on A. It is the only side that can be
 *              dirty — B is a frozen copy and nothing can edit it.
 */

import React from 'react';
import SearchableDropdown from './SearchableDropdown';
import DateInput from './DateInput';

export const BLANK_COMPARE = '__blank__';

function BlendWorkBar({
  contextKind, contextLabel, isDirty,
  loadOptions, onLoad, loadedValue,
  compareOptions, compareId, onCompare,
  asOf, onAsOf,
  specOptions, specId, onSpec,
}) {
  return (
    <div className="blend-workbar">
      {/* A — the blend on the bench, and the only editable one. Chosen first,
          whether that means loading something to work from or leaving it blank
          and building up. Shows its own name, so there is no separate context
          chip; the icon says whether it came from a saved blend, a formulation,
          or nothing at all. */}
      <span className={`bwb-slot bwb-a bwb-${contextKind}`}>
        <span className="blend-ab-tag is-a">A</span>
        <span className="material-symbols-rounded bwb-kind" title={contextLabel}>
          {contextKind === 'blend' ? 'bookmark'
            : contextKind === 'formulation' ? 'science' : 'edit_note'}
        </span>
        <SearchableDropdown
          value={loadedValue || ''}
          onChange={(v) => v && onLoad(v)}
          options={loadOptions}
          placeholder={contextLabel || 'Start blank, or load…'}
        />
        {isDirty && (
          <span className="bwb-dirty" title="This differs from what was last saved or loaded">
            unsaved
          </span>
        )}
      </span>

      {/* B — something to hold A against. Read-only and optional: empty is the
          normal state, and picking one turns the comparison on. */}
      <span className={`bwb-slot bwb-b${compareId ? ' on' : ''}`}>
        <span className="blend-ab-tag is-b">B</span>
        <SearchableDropdown
          value={compareId || ''}
          onChange={onCompare}
          options={compareOptions}
          placeholder="Compare with…"
        />
      </span>

      {/* The two settings that change every figure on screen. Labels sit inside
          rather than above: a stacked label doubles the bar's height to repeat
          what the control already says. */}
      <span className="bwb-setting bwb-asof" title="Every price on screen is as at this date">
        <span className="bwb-setting-label">Priced</span>
        <DateInput value={asOf} onChange={onAsOf} />
      </span>

      <span className="bwb-setting bwb-spec" title="Hold the blend against a target specification">
        <span className="bwb-setting-label">Spec</span>
        <SearchableDropdown
          value={specId ? String(specId) : ''}
          onChange={(v) => onSpec(v ? Number(v) : null)}
          options={specOptions}
          placeholder="None"
        />
      </span>

    </div>
  );
}

export default BlendWorkBar;
